import type { SelectOption } from '../../types';
import type { ReleaseGroupRouteParams } from '../projects/project_grouping/types';
import type { RevisionRouteParams } from '../projects/projectDashboardTypes';

export type ReportType = 'licensing' | 'sbom' | 'vulnerability' | 'remediationGuidance';

// This is the user-facing name of the report, which is displayed in the heading and sidebar.
export type ReportHeading = 'Licensing' | 'SBOM' | 'Vulnerabilities' | 'Remediation Guidance';

export type ReportsRouteParams = {
  reportType: ReportType;
};

//  If we are on the project page, then we will have the project revision params.
//  We include them in this type, as we use it to check if we are on
type ProjectReportParams = ReportsRouteParams & RevisionRouteParams;
type ReleaseGroupReportParams = ReportsRouteParams & ReleaseGroupRouteParams;

export type ReportsV3Params =
  | ProjectReportParams
  | ReleaseGroupReportParams;

/**
 * Type guard for checking if the params are for a release group scope.
 */
export const isReleaseGroupScope = (params: ReportsV3Params): params is ReleaseGroupReportParams => {
  //  Despite being typed as a number, the projectGroupId is really a string which needs to be cast to a number.
  return 'projectGroupId' in params && Number.isFinite(Number(params.projectGroupId));
};

/**
 * The two types of report flows.
 */
export type ReportFlow = 'standard' | 'custom';

/**
 * These represent the possible query params for the reports v3 page.
 * TODO: this will eventually hold all the possible report options, so that sets of options
 * can be shared (even if they are not "saved").
 */
export type ReportsV3QueryParams = Readonly<Partial<{
  flow: ReportFlow;
  format: ReportV3Format;
}>>;

type LicensingFormat = 'HTML' | 'PDF' | 'CSV' | 'MD' | 'TXT';
type LicensingFormatLabel = 'HTML' | 'PDF' | 'CSV' | 'Markdown' | 'Plain Text';
export type LicensingFormatOption = SelectOption<LicensingFormat, LicensingFormatLabel>;

type SBOMFormat = 'SPDX' | 'SPDX_JSON' | 'CYCLONEDX_JSON' | 'CYCLONEDX_XML';
type SBOMFormatLabel = 'SPDX' | 'SPDX (JSON)' | 'CycloneDX (JSON)' | 'CycloneDX (XML)';
export type SBOMFormatOption = SelectOption<SBOMFormat, SBOMFormatLabel>;

type VulnerabilityFormat = 'PDF';
type VulnerabilityFormatLabel = VulnerabilityFormat;
export type VulnerabilityFormatOption = SelectOption<VulnerabilityFormat, VulnerabilityFormatLabel>;

type RemediationGuidanceFormat = 'PDF' | 'HTML' | 'JSON';
type RemediationGuidanceFormatLabel = RemediationGuidanceFormat;
export type RemediationGuidanceFormatOption = SelectOption<RemediationGuidanceFormat, RemediationGuidanceFormatLabel>;

// TODO: rename after GA
export type ReportV3Format = LicensingFormat | SBOMFormat | VulnerabilityFormat | RemediationGuidanceFormat;

export type AnyReportFormatOption =
  | LicensingFormatOption
  | SBOMFormatOption
  | VulnerabilityFormatOption
  | RemediationGuidanceFormatOption;

const licensingFormatSet = new Set<LicensingFormat>(['HTML', 'PDF', 'CSV', 'MD', 'TXT']);
const sbomFormatSet = new Set<SBOMFormat>(['SPDX', 'SPDX_JSON', 'CYCLONEDX_JSON', 'CYCLONEDX_XML']);
const vulnerabilityFormatSet = new Set<VulnerabilityFormat>(['PDF']);
const remediationGuidanceFormatSet = new Set<RemediationGuidanceFormat>(['PDF', 'HTML', 'JSON']);

const formatSets: Readonly<Record<ReportType, Set<ReportV3Format>>> = {
  licensing: licensingFormatSet,
  sbom: sbomFormatSet,
  vulnerability: vulnerabilityFormatSet,
  remediationGuidance: remediationGuidanceFormatSet,
};

/**
 * Checks if the format is valid for the given report type.
 * @param reportType The report type to check the format for.
 * @param format The format to check.
 * @returns True if the format is valid for the given report type, false otherwise.
 */
export const isValidReportFormat = (reportType: ReportType, format: ReportV3Format): boolean => {
  return formatSets[reportType].has(format);
};
